import {
  type WorkspaceAgentRunFinishedEvent,
  type WorkspaceAgentRunStartedEvent,
} from '~/components/v2/components/workspace/workspace-agent-panel/types/workspaceAgentRun';

export type WorkspaceAgentCancelScope = {
  sessionId: string;
  sendGeneration: number;
  cancelledRunId: string | null;
};

export type WorkspaceAgentCancelRunGuardState = {
  sendGeneration: number;
  cancelScope: WorkspaceAgentCancelScope | null;
  suppressRunStartedSessionId: string | null;
  suppressedRunIds: Set<string>;
  absorbedStaleRunFinishedAfterResend: boolean;
  stashedRunStarted: WorkspaceAgentRunStartedEvent | null;
};

export type EvaluateRunStartedResult = { action: 'accept' } | { action: 'suppress' };

export type EvaluateRunFinishedResult = {
  isSuppressedCancelledRun: boolean;
  shouldClearAwaitingRunStart: boolean;
};

export type CancelFailedResult = {
  replayRunStarted: WorkspaceAgentRunStartedEvent | null;
  shouldClearAwaitingRunStart: boolean;
};

export const createWorkspaceAgentCancelRunGuard = (): WorkspaceAgentCancelRunGuardState => ({
  sendGeneration: 0,
  cancelScope: null,
  suppressRunStartedSessionId: null,
  suppressedRunIds: new Set(),
  absorbedStaleRunFinishedAfterResend: false,
  stashedRunStarted: null,
});

export const resetWorkspaceAgentCancelRunGuard = (state: WorkspaceAgentCancelRunGuardState): void => {
  state.sendGeneration = 0;
  state.cancelScope = null;
  state.suppressRunStartedSessionId = null;
  state.suppressedRunIds.clear();
  state.absorbedStaleRunFinishedAfterResend = false;
  state.stashedRunStarted = null;
};

export const beginSend = (
  state: WorkspaceAgentCancelRunGuardState,
  sessionId: string | null,
): { sendGeneration: number } => {
  if (sessionId !== null && sessionId === state.suppressRunStartedSessionId) {
    const cancelledRunId = state.cancelScope?.cancelledRunId ?? null;

    if (cancelledRunId !== null) {
      state.suppressedRunIds.add(cancelledRunId);

      if (state.cancelScope !== null) {
        state.cancelScope = { ...state.cancelScope, cancelledRunId: null };
      }
    } else if (state.cancelScope !== null) {
      state.absorbedStaleRunFinishedAfterResend = false;
    }

    state.suppressRunStartedSessionId = null;
  }

  state.sendGeneration += 1;

  return { sendGeneration: state.sendGeneration };
};

export const beginCancel = (
  state: WorkspaceAgentCancelRunGuardState,
  params: {
    sessionId: string;
    activeRunId: string | null;
    isAwaitingRunStart: boolean;
  },
): { shouldSuppressRunStarted: boolean } => {
  state.stashedRunStarted = null;

  const shouldSuppressRunStarted = params.activeRunId !== null || params.isAwaitingRunStart;

  if (shouldSuppressRunStarted) {
    state.suppressRunStartedSessionId = params.sessionId;
    state.cancelScope = {
      sessionId: params.sessionId,
      sendGeneration: state.sendGeneration,
      cancelledRunId: params.activeRunId,
    };
  }

  return { shouldSuppressRunStarted };
};

export const evaluateRunStarted = (
  state: WorkspaceAgentCancelRunGuardState,
  event: WorkspaceAgentRunStartedEvent,
): EvaluateRunStartedResult => {
  if (state.suppressedRunIds.has(event.runId)) {
    return { action: 'suppress' };
  }

  if (event.runId === state.cancelScope?.cancelledRunId) {
    if (state.cancelScope !== null && state.cancelScope.sessionId === event.sessionId) {
      state.stashedRunStarted = event;
    }

    return { action: 'suppress' };
  }

  if (event.sessionId === state.suppressRunStartedSessionId) {
    state.suppressedRunIds.add(event.runId);

    if (state.cancelScope !== null && state.cancelScope.sessionId === event.sessionId) {
      state.stashedRunStarted = event;
    }

    return { action: 'suppress' };
  }

  return { action: 'accept' };
};

export const evaluateRunFinished = (
  state: WorkspaceAgentCancelRunGuardState,
  event: WorkspaceAgentRunFinishedEvent,
  activeSessionId: string | null,
): EvaluateRunFinishedResult => {
  const isSuppressedCancelledRun =
    state.suppressedRunIds.has(event.runId) || event.runId === state.cancelScope?.cancelledRunId;

  state.suppressedRunIds.delete(event.runId);

  if (event.runId === state.cancelScope?.cancelledRunId && state.cancelScope !== null) {
    state.cancelScope = { ...state.cancelScope, cancelledRunId: null };
  }

  if (state.stashedRunStarted?.runId === event.runId) {
    state.stashedRunStarted = null;
  }

  if (event.sessionId === state.suppressRunStartedSessionId) {
    state.suppressRunStartedSessionId = null;

    if (state.cancelScope !== null && state.sendGeneration <= state.cancelScope.sendGeneration) {
      state.cancelScope = null;
    }
  }

  const hasNewerSendAfterCancel = state.cancelScope !== null && state.sendGeneration > state.cancelScope.sendGeneration;

  let shouldClearAwaitingRunStart = false;

  if (event.sessionId === activeSessionId) {
    if (hasNewerSendAfterCancel && !isSuppressedCancelledRun && !state.absorbedStaleRunFinishedAfterResend) {
      state.suppressedRunIds.add(event.runId);
      state.absorbedStaleRunFinishedAfterResend = true;
      state.cancelScope = null;
    } else if (!isSuppressedCancelledRun && !hasNewerSendAfterCancel) {
      shouldClearAwaitingRunStart = true;
    } else if (!isSuppressedCancelledRun && state.absorbedStaleRunFinishedAfterResend) {
      shouldClearAwaitingRunStart = true;
    }
  }

  return { isSuppressedCancelledRun, shouldClearAwaitingRunStart };
};

export const cancelSucceeded = (state: WorkspaceAgentCancelRunGuardState, shouldSuppressRunStarted: boolean): void => {
  state.absorbedStaleRunFinishedAfterResend = false;
  state.stashedRunStarted = null;

  if (!shouldSuppressRunStarted) {
    state.suppressRunStartedSessionId = null;
  }
};

export const cancelFailed = (state: WorkspaceAgentCancelRunGuardState): CancelFailedResult => {
  const replayRunStarted = state.stashedRunStarted;

  state.stashedRunStarted = null;
  state.suppressRunStartedSessionId = null;
  state.suppressedRunIds.clear();
  state.cancelScope = null;
  state.absorbedStaleRunFinishedAfterResend = false;

  return {
    replayRunStarted,
    shouldClearAwaitingRunStart: replayRunStarted === null,
  };
};
